'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';

export type AssetPreviewMode = 'static' | 'animated';

export default function AssetPreview({
  src,
  alt,
  mode,
  className,
  style,
}: {
  src: string;
  alt: string;
  mode: AssetPreviewMode;
  className?: string;
  style?: CSSProperties;
}) {
  const holder = useRef<HTMLSpanElement>(null);
  const [enabled, setEnabled] = useState(mode === 'static');

  useEffect(() => {
    if (mode === 'static') {
      setEnabled(true);
      return;
    }
    const element = holder.current;
    if (!element) return;
    if (typeof IntersectionObserver === 'undefined') {
      setEnabled(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setEnabled(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    observer.observe(element);
    return () => observer.disconnect();
  }, [mode, src]);

  return (
    <span ref={holder} style={{ display: 'contents' }}>
      {enabled ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={className}
          style={style}
        />
      ) : null}
    </span>
  );
}
